// Part 1 of OneLoan.in blog corpus — original SEO-optimised content.
// All figures are indicative for 2026 in India and subject to lender discretion.

export const BLOGS_PART_1 = [
  {
    slug: "personal-loan-eligibility-guide-2025",
    title: "Personal Loan Eligibility in 2026: Complete Guide for Salaried & Self-Employed",
    metaTitle: "Personal Loan Eligibility 2026: Salaried & Self-Employed Guide",
    metaDescription: "Complete personal loan eligibility guide for India 2026. CIBIL, income, FOIR, age & employment rules + 9 approval hacks. Salaried & self-employed.",
    keywords: ["personal loan eligibility", "personal loan 2026", "loan eligibility salaried", "personal loan CIBIL", "FOIR calculator"],
    category: "Personal Loan Guides",
    author: "OneLoan Editorial",
    authorRole: "Lending Specialists",
    date: "Dec 5, 2025",
    updated: "Dec 5, 2025",
    readTime: "11 min read",
    excerpt: "Most personal loan rejections happen for predictable reasons. Here is the 2026 eligibility playbook — CIBIL bands, FOIR limits, bank-by-bank cutoffs and 9 practical ways to push your approval odds above 90%.",
    tags: ["Eligibility", "Salaried", "Self-Employed", "CIBIL"],
    relatedProduct: "personal",
    body: `Lenders today combine credit-bureau data, GST/ITR signals and bank-statement analytics to approve loans in minutes. Yet ~3 in 10 personal-loan applications in India still get rejected. Most rejections come from a small set of fixable issues. This guide walks through the exact 2026 eligibility yardsticks used by leading Banks and NBFCs — and how to position your profile so you actually get the offer you want.

> **KEY TAKEAWAY:** A CIBIL of 750+, FOIR under 50% and 12+ months of stable income are the three levers that move 80% of decisions. Fix those first, everything else is fine-tuning.

## Quick eligibility snapshot

| Parameter | Salaried | Self-Employed |
|---|---|---|
| Age | 21 – 60 yrs | 25 – 65 yrs |
| Min monthly income | ₹25,000 | ₹3 Lakh annual ITR |
| CIBIL score | 700+ (750+ for best rates) | 720+ |
| Employment / business vintage | 12 months total, 6 in current job | 2+ years business vintage |
| FOIR | ≤ 55% | ≤ 60% |
| Citizenship | Indian resident | Indian resident |
| Loan amount range | ₹50,000 – ₹50 Lakh | ₹1 Lakh – ₹50 Lakh |

Use our [eligibility calculator](/tools/eligibility-calculator) for an instant estimate based on your income.

## Eligibility for salaried professionals

Salaried applicants are easier for lenders to underwrite because income is documented monthly. The signal lenders care about most is **stability of cash flow**.

### Income brackets and what they unlock
- **₹25,000 – ₹40,000/month:** Most private banks + NBFCs. Expect higher rates (14–22%).
- **₹40,001 – ₹75,000/month:** Premium personal loans from HDFC, ICICI, Axis. Rates in the 11–15% band.
- **₹75,001+/month:** Pre-approved offers, lowest rates (9.99–12%), faster disbursal.

### Employer category matters
Banks classify employers into A/B/C/D tiers. A Category A employer (top listed companies, MNCs, PSUs) can unlock 1–2% lower rates than a Category C/D employer for the exact same salary and CIBIL profile.

### Employment vintage
You need at least **12 months of total work experience** and ideally **6 months in your current company**. Switching jobs immediately before applying is the single most common avoidable mistake.

## Eligibility for self-employed and professionals

Self-employed applicants — including doctors, CAs, consultants and business owners — face a slightly higher bar because income variability is higher.

### Income proof options
- Latest 2 years of ITR with computation
- 6–12 months business bank statements (for current account)
- GST returns (last 12 months) if registered
- Audited Profit & Loss statement (above ₹1 Cr turnover)

### Vintage requirement
- **Business / firm:** 2 years minimum (some lenders accept 1 year for professionals)
- **Profession (Doctor / CA / Architect):** 2 years post-registration

### Pro tip — show steady GST or current-account credits
Lenders weight a clean 12-month bank statement (no bounced cheques, regular credits) more heavily than a single high-ITR year. Consistency beats spikes.

## CIBIL score requirements explained

Your CIBIL (or Experian/Equifax) score is the single biggest lever in pricing.

| CIBIL band | What to expect |
|---|---|
| 800+ | Best rates, often pre-approved, lowest processing fees |
| 750 – 799 | All prime lenders approve, rates 10–13% |
| 700 – 749 | Approval possible, rates 13–18% |
| 650 – 699 | NBFCs only, rates 18–24% |
| Below 650 | Rejections from most lenders |

A score below 700 doesn't mean you can't borrow — it means you'll pay a premium. Read our [step-by-step CIBIL repair guide](/learn/improve-cibil-score-fast) to push your score upward in 3–6 months.

## FOIR — the make-or-break ratio

FOIR (Fixed Obligation to Income Ratio) is the percentage of your monthly income already committed to EMIs.

### The formula
\`FOIR = (Existing EMIs + Proposed new EMI) ÷ Net monthly income × 100\`

Most lenders cap FOIR at **50–55%** for salaried and **60%** for self-employed.

### Worked example
- Monthly take-home: ₹60,000
- Existing EMIs (home + car): ₹18,000
- Maximum allowable FOIR: 55% → ₹33,000
- Maximum new EMI you can take: ₹33,000 − ₹18,000 = **₹15,000**
- At 12% for 5 years → eligible loan ≈ **₹6.75 Lakh**

If you want a bigger ticket, either reduce existing EMIs (prepay your car loan) or extend tenure to lower the EMI.

## Bank-by-bank minimum criteria (indicative 2026)

| Lender | Min income | Min CIBIL | Vintage |
|---|---|---|---|
| HDFC Bank | ₹25,000 | 720 | 1 year |
| ICICI Bank | ₹30,000 | 720 | 2 years |
| Axis Bank | ₹25,000 | 720 | 1 year |
| Kotak Mahindra | ₹25,000 | 750 | 1 year |
| IDFC FIRST | ₹25,000 | 700 | 1 year |
| Bajaj Finance | ₹22,000 | 685 | 1 year |
| Tata Capital | ₹25,000 | 700 | 1 year |
| InCred / Poonawalla | ₹20,000 | 680 | 1 year |

Compare live offers across all 16 lenders on our [Personal Loan page](/personal-loan).

## Top 7 reasons personal loans get rejected

1. CIBIL below the lender's cutoff (most common)
2. Recent multiple hard enquiries (5+ in 90 days flags "credit hungry")
3. FOIR breach due to existing EMIs or credit-card debt
4. Job switch within 3 months of applying
5. Employer not on the lender's approved list
6. Bounced cheques or returned NACH mandates in last 6 months
7. Mismatch between salary slip income and bank-credit income

## 9 ways to improve your approval chances

1. **Pull your free CIBIL report** before applying and dispute errors.
2. **Reduce credit-card utilisation** below 30% of your limit.
3. **Don't close old credit cards** — long history helps your score.
4. **Avoid 5+ loan enquiries** in a 90-day window.
5. **Apply with one strong lender first**, not 4 banks at once.
6. **Add a co-applicant** (spouse / parent) with a strong CIBIL.
7. **Choose longer tenure** to bring your EMI under FOIR limits.
8. **Ensure salary credits show in your salary account** — not a different account.
9. **Maintain 3+ months of clean bank statement** before applying.

## Documents required

- PAN card and Aadhaar
- Last 3 months salary slips OR last 2 years ITR
- Last 3 months bank statement (salary account)
- Address proof (utility bill / rent agreement)
- One passport-size photograph
- Form 16 (for premium banks)

## Approval timeline

- **Pre-approved customer:** 2 minutes – 4 hours
- **Salaried, complete docs:** 24 – 72 hours
- **Self-employed, full underwriting:** 3 – 7 working days

Disbursal happens directly to your bank account once your e-mandate is set up.

> **PRO TIP:** Start by getting a free, no-obligation eligibility check on OneLoan. We do a soft enquiry which **does not impact your CIBIL** but reveals which lenders will actually approve you. That way you only formally apply where you're already a near-certain "yes".`,
    faqs: [
      { q: "What is the minimum salary for a personal loan in India in 2026?", a: "Most banks require ₹25,000 net monthly salary for prime products. NBFCs like Bajaj Finance and Poonawalla Fincorp accept from ₹20,000–₹22,000 with stronger CIBIL." },
      { q: "Can I get a personal loan with a CIBIL score below 700?", a: "Yes, NBFCs and select banks lend down to a 680 CIBIL, but interest rates are 18–26% and processing fees are higher. Improving your score before applying usually saves more than the loan amount itself." },
      { q: "Does checking my eligibility on OneLoan affect my CIBIL score?", a: "No. We perform a soft enquiry to match you with lenders — this is invisible to other lenders and has zero impact on your CIBIL." },
      { q: "How is FOIR calculated and why does it matter?", a: "FOIR = (all monthly EMIs ÷ monthly income) × 100. Banks cap it around 50–55% for salaried. If your existing EMIs already use up that headroom, your new loan amount will be reduced." },
      { q: "Will a co-applicant improve my eligibility?", a: "Yes — adding a spouse, parent or sibling with stable income and 750+ CIBIL can significantly increase the approved loan amount and reduce the rate." },
    ],
  },

  {
    slug: "best-personal-loan-interest-rates-india",
    title: "Best Personal Loan Interest Rates in India 2026: Lowest Rates Compared",
    metaTitle: "Best Personal Loan Interest Rates 2026 (India) — Lowest Compared",
    metaDescription: "Compare lowest personal loan interest rates in India 2026 across 16 banks & NBFCs. Rates from 9.99%, processing fees, hidden charges & negotiation tips.",
    keywords: ["personal loan interest rate", "lowest personal loan rate", "best personal loan India 2026", "HDFC personal loan rate", "ICICI personal loan rate"],
    category: "Personal Loan Guides",
    author: "OneLoan Editorial",
    authorRole: "Lending Specialists",
    date: "Dec 4, 2025",
    updated: "Dec 5, 2025",
    readTime: "10 min read",
    excerpt: "Personal loan rates in India start at 9.99% in 2026 — but the rate you actually get depends on 6 factors most borrowers ignore. Here is the full bank-by-bank breakdown and a 5-minute negotiation script.",
    tags: ["Interest Rates", "Comparison", "Banks", "NBFCs"],
    relatedProduct: "personal",
    body: `If you are shopping for a personal loan in 2026, the headline rate you see in an advertisement is rarely the rate you get. Banks publish a "starting from" rate that is reserved for top-tier borrowers — your actual rate depends on six underwriting inputs. This guide gives you the latest indicative rates across all 16 lenders on OneLoan, the variables that move the needle, and a tested negotiation playbook.

> **KEY TAKEAWAY:** Personal loan rates in India range from **9.99% to 28% p.a.** in 2026. Your CIBIL, employer category and income decide where you land. A 1% rate cut on a ₹10L / 5-year loan saves roughly ₹30,000.

## Lowest personal loan rates in India — indicative 2026

| Lender | Starting Rate | Processing Fee | Max Amount |
|---|---|---|---|
| Axis Bank | 9.99% | Up to 2.0% | ₹40 Lakh |
| IDFC FIRST Bank | 9.99% | Up to 3.5% | ₹1 Crore |
| IndusInd Bank | 9.99% | Up to 3.0% | ₹50 Lakh |
| HDFC Bank | 10.50% | Up to 2.5% | ₹40 Lakh |
| ICICI Bank | 10.85% | Up to 2.5% | ₹50 Lakh |
| Kotak Mahindra | 10.99% | Up to 3.0% | ₹35 Lakh |
| Yes Bank | 10.99% | Up to 2.5% | ₹40 Lakh |
| Bajaj Finance | 11.00% | Up to 3.93% | ₹55 Lakh |
| Bandhan Bank | 11.55% | Up to 2.5% | ₹25 Lakh |
| Axis Finance | 11.50% | Up to 2.0% | ₹50 Lakh |
| Poonawalla Fincorp | 10.99% | Up to 3.0% | ₹50 Lakh |
| Tata Capital | 11.99% | Up to 2.75% | ₹35 Lakh |
| InCred Finance | 11.99% | Up to 3.0% | ₹15 Lakh |
| Aditya Birla Finance | 12.00% | Up to 2.5% | ₹50 Lakh |
| Piramal Finance | 12.99% | Up to 3.0% | ₹25 Lakh |
| SMFG India Credit | 12.50% | Up to 3.0% | ₹30 Lakh |

Live, side-by-side offers — including your specific eligibility — on our [Personal Loan comparison page](/personal-loan).

## 6 factors that decide your actual interest rate

1. **CIBIL score** — biggest single lever. A move from 720 to 780 typically saves 1.5–2% on the rate.
2. **Employer category** — Cat A (top MNCs, Fortune 500, PSUs) gets 1–2% lower than Cat C.
3. **Net monthly income** — above ₹75K, you unlock premium pricing tiers.
4. **Existing banking relationship** — pre-approved salary-account customers get the sharpest rates.
5. **Loan amount and tenure** — larger amounts (₹10L+) over 3–5 years often qualify for special pricing.
6. **Risk-based pricing model** of the lender — NBFCs price for risk, banks price for relationship.

## Reducing reducing-balance vs flat rate — read this before signing

Personal loans in India are quoted on a **reducing-balance** basis. Some unscrupulous DSAs convert this into a **flat rate** to make it look lower. They are not the same.

### Example
- Loan: ₹5,00,000 for 36 months
- Reducing rate: 12% → EMI ≈ ₹16,607
- Flat rate equivalent: ~6.7% (looks lower but total cost is identical)

If a lender or agent quotes a flat rate without showing the EMI, ask for the **reducing-balance APR** before signing. Use our [EMI calculator](/tools/emi-calculator) to verify any quote in 10 seconds.

## Hidden charges to scan for

- **Processing fee:** 1–3.93% of the loan amount + GST. Negotiable for high-CIBIL profiles.
- **Pre-payment / foreclosure:** 0–4% on outstanding. Most banks waive after 6–12 months.
- **Part-payment:** 2–5% on the part-paid amount (some lenders allow once a year free).
- **Late payment penalty:** 2% per month on the overdue EMI + GST.
- **GST on charges:** 18% applicable on all loan-related fees.
- **Loan cancellation:** ₹1,000 – ₹3,000 + GST after disbursal.

> **PRO TIP:** Compare the **Annual Percentage Rate (APR)**, not just the headline interest rate. APR bundles processing fees and other charges into a single number that reflects the true cost of borrowing.

## How to negotiate a better rate — a 5-minute script

1. **Pull a competing offer first.** Use OneLoan to get 3 live offers. Print the lowest one.
2. **Call your primary bank** (the one where your salary lands). Ask for their pre-approved offer and their best rate for a personal loan.
3. **Counter-offer:** "Bank X is offering me 11.2% with 1.5% fee. Can you match or beat it?" — premium-banking customers commonly get 0.5–1% off this way.
4. **Ask for processing-fee waiver.** Even if rate is fixed, fee is often discretionary by the branch.
5. **Walk away once.** Many lenders activate a "save-the-deal" team that comes back within 24 hours with a better number.

## Salaried vs self-employed — rate differential

| Profile | Typical rate band 2026 |
|---|---|
| Premium salaried (Cat A, ₹1L+ income) | 9.99% – 11.5% |
| Mid-tier salaried (Cat B/C) | 11.5% – 15% |
| Self-employed professional (Doctor / CA) | 11% – 14% |
| Self-employed business (ITR + GST) | 13% – 18% |
| Self-employed (cash income, weak documentation) | 18% – 26% |

## When to choose an NBFC over a bank

- Your CIBIL is below 700
- You need same-day disbursal
- Your employer is not on a bank's approved list
- You are self-employed with under 3 years vintage

NBFCs price higher but underwrite more flexibly. Bajaj Finance, Tata Capital, Poonawalla Fincorp and InCred are the most active in this segment.

## The bottom line

The lowest rates in 2026 (9.99–10.5%) are reserved for: CIBIL 780+, Cat A employer, ₹75K+ income, no FOIR breach. If you don't tick all four boxes, focus on improving the weakest one — every 0.5% rate cut compounds to meaningful savings over the loan life.

> **NOTE:** All rates above are indicative and subject to lender discretion. Final rates are confirmed only on credit assessment. Apply with confidence on [OneLoan](/apply) for a soft, no-impact match across all 16 lenders.`,
    faqs: [
      { q: "What is the lowest personal loan interest rate in India in 2026?", a: "Indicative starting rates begin at 9.99% p.a. on a reducing-balance basis at Axis Bank, IDFC FIRST and IndusInd for premium profiles (CIBIL 780+, Category A employer, high income)." },
      { q: "Is a 12% personal loan a good interest rate?", a: "For a salaried borrower with a 720–760 CIBIL, 12% is a competitive market rate in 2026. Premium profiles can negotiate down to 10.5–11%; below-700 CIBIL borrowers typically pay 14–22%." },
      { q: "How much can I save by reducing my personal-loan rate by 1%?", a: "On a ₹10 Lakh loan over 5 years, dropping the rate from 14% to 13% saves approximately ₹30,000 in total interest. Always check savings using our balance-transfer calculator before refinancing." },
      { q: "Are NBFC personal-loan rates higher than banks?", a: "Generally yes — NBFCs price 1–4% higher because they underwrite riskier profiles. But NBFCs are faster and more flexible on CIBIL, vintage and employer category." },
      { q: "Can processing fees be negotiated?", a: "Yes. Processing fees of 2–3% are often discretionary and can be reduced by 50% or more if you have a competing offer in hand or a strong banking relationship." },
    ],
  },

  {
    slug: "instant-personal-loan-without-itr",
    title: "Instant Personal Loan Without ITR or Salary Slip: What's Actually Possible in 2026",
    metaTitle: "Personal Loan Without ITR or Salary Slip — 2026 Options",
    metaDescription: "Need a personal loan without ITR or salary slip? Here are the real lenders, alternative income proof options, eligibility & how to apply in 2026.",
    keywords: ["personal loan without ITR", "personal loan without salary slip", "instant personal loan no documents", "loan against bank statement"],
    category: "Personal Loan Guides",
    author: "OneLoan Editorial",
    authorRole: "Lending Specialists",
    date: "Dec 3, 2025",
    updated: "Dec 5, 2025",
    readTime: "9 min read",
    excerpt: "If you don't have an ITR or salary slip ready, you are not out of options. Lenders in 2026 underwrite on bank statements, GST data, UPI flows and pre-approved offers. Here is exactly what is feasible — and what is not.",
    tags: ["No Documents", "Instant", "Self-Employed", "Salaried"],
    relatedProduct: "personal",
    body: `Search volume for "personal loan without ITR" and "loan without salary slip" has grown sharply in India over the last 3 years. The honest answer in 2026: **yes, it is possible** — but the lenders, ticket sizes and rates are different from a standard personal loan. This guide separates marketing claims from what actually gets approved.

> **KEY TAKEAWAY:** No reputable lender approves a personal loan with zero documents. What lenders do accept is alternative income proof — bank statements, GST returns, UPI flows, or a strong pre-existing relationship — in place of conventional ITR / salary slips.

## What "no documents" really means

Lenders are legally required (under RBI and KYC norms) to verify identity, address and income. So "no document" usually means one of three things:

1. **Pre-approved offers** to existing customers — KYC is already on file.
2. **Alternative income proof** instead of ITR / salary slip (bank statement, GST, UPI).
3. **Account aggregator (AA) framework** — instant consent-based pulling of your bank/GST/EPF data, no physical paperwork.

If a "lender" promises a loan with literally zero verification, treat it as a scam and avoid.

## Who can get a personal loan without ITR or salary slip?

### 1. Salaried with bank-statement underwriting
If your salary credits regularly to a savings account but you can't access salary slips (HR delay, new joinee), most NBFCs and 4–5 banks underwrite purely on **6 months of bank statements** showing consistent salary credits with the same description.

### 2. Pre-approved bank customers
If you have a salary account, fixed deposit or relationship of 2+ years with a bank, you very likely have a **pre-approved personal loan** waiting in your net-banking. Disbursal: 30 seconds to a few hours. No documents required.

### 3. Self-employed with GST or UPI flows
Self-employed merchants who don't file ITR or whose ITR understates income can borrow on the basis of:
- **GST returns** (last 12 months)
- **UPI / QR payment history** (Paytm for Business, PhonePe Smart Store, BharatPe statements)
- **Current account bank statements** (12 months)

Lenders like Bajaj Finance, Aditya Birla Finance and InCred Finance run dedicated "merchant loan" products on this data.

### 4. Account Aggregator (AA) based instant lending
The Account Aggregator framework lets you instantly share your bank, GST and EPFO data with a lender — encrypted, consent-based, and revocable. Many digital-first NBFCs disburse within hours using AA data instead of physical ITR.

## Documents you'll likely still need

Even in a "no-ITR" flow, expect to provide:
- **PAN card** (mandatory)
- **Aadhaar** for KYC
- **Bank account number** for disbursal
- **6–12 months bank statement** OR consent on AA framework
- **Selfie + live photo** for video KYC

## Lenders that offer instant personal loans on alternative income proof

| Lender | Alternative proof accepted | Loan range | Disbursal |
|---|---|---|---|
| Bajaj Finance | Bank statement, GST, UPI | ₹30K – ₹40L | Same day |
| Tata Capital | Bank statement, GST | ₹50K – ₹35L | 24 hours |
| Aditya Birla Finance | Bank statement, GST, AA | ₹50K – ₹50L | Same day |
| Poonawalla Fincorp | Bank statement, AA | ₹50K – ₹50L | Same day |
| InCred Finance | Bank statement, GST | ₹50K – ₹15L | 24–48 hours |
| Kotak Mahindra (811) | Pre-approved relationship | ₹50K – ₹10L | Instant |
| IDFC FIRST Bank | Bank statement, AA | ₹50K – ₹1Cr | 24 hours |

Check your eligibility across all 16 lenders without uploading documents at [OneLoan Apply](/apply).

## What you should expect (and not expect)

### Realistic
- Loan size: ₹50,000 to ₹15 Lakh (rarely higher without formal income proof)
- Interest rate: 16% to 26% p.a. — 3–8% premium over standard rates
- Tenure: 6 to 60 months
- Processing fee: 2–4% + GST
- Disbursal: Same day to 72 hours

### Unrealistic
- ₹50 Lakh loan with no income proof
- Rate of 10% with no documentation
- Approval without PAN or Aadhaar
- Loan without any bank statement check

## Top 6 ways to strengthen a no-ITR application

1. **Keep 6 months of clean bank statement** — no bounced cheques, regular credits.
2. **Link your UPI / QR data** (Paytm for Business, PhonePe Smart Store) for merchant loans.
3. **Use the AA framework** to share encrypted bank + GST data instantly.
4. **Apply to your salary-account bank first** — pre-approved offers don't need ITR.
5. **Provide GST returns** even if not mandatory — improves underwriting.
6. **Maintain a high CIBIL** — 750+ compensates for limited income docs.

## Red flags to avoid

- Lenders asking for upfront fees before disbursal (legitimate lenders deduct from disbursal)
- WhatsApp-only loan agents with no website or RBI registration
- Promises of "100% approval" or "no CIBIL check"
- Apps without an RBI-listed Authorised NBFC partner
- Requests to share OTP, debit-card PIN, or net-banking password

> **PRO TIP:** OneLoan only routes you to RBI-registered Banks and NBFCs. Every offer is from a regulated lender. No upfront fees, no hidden charges, and we never ask for your OTP or banking PIN.

## The bottom line

A personal loan without ITR or salary slip in 2026 is real — but it lives in the alternative-income-proof corridor. Use [OneLoan Apply](/apply) and select **"Alternative income proof"** to see which lenders will say yes to your specific situation in under 5 minutes.`,
    faqs: [
      { q: "Can I get a personal loan without an ITR in 2026?", a: "Yes. Lenders like Bajaj Finance, Tata Capital, Aditya Birla Finance and Poonawalla Fincorp offer personal loans on 6–12 months of bank statement or GST data instead of an ITR. Rates are typically 2–4% higher than standard products." },
      { q: "What is the maximum loan amount without ITR?", a: "Most lenders cap no-ITR personal loans between ₹50,000 and ₹15 Lakh. Above that ticket, formal ITR or audited financials are usually required." },
      { q: "Will I need to provide my salary slips?", a: "Not necessarily. If your salary credits are clearly visible in your bank statement with a consistent description, lenders can verify income from the statement alone." },
      { q: "What is the Account Aggregator framework?", a: "AA is an RBI-regulated, consent-based system that lets you instantly share encrypted financial data (bank, GST, EPF) with a lender. No physical paperwork, and you can revoke consent anytime." },
      { q: "Are no-document loans safe?", a: "Loans through RBI-registered Banks and NBFCs are safe. Avoid any lender or app that asks for upfront fees, OTPs or banking PINs, or that has no traceable RBI registration." },
    ],
  },

  {
    slug: "business-loan-without-collateral",
    title: "Business Loan Without Collateral 2026: ₹2 Crore Unsecured Loan Playbook for MSMEs",
    metaTitle: "Business Loan Without Collateral 2026: Up to ₹2 Crore",
    metaDescription: "Unsecured business loans up to ₹2 Crore in India 2026. Eligibility, CGTMSE-backed schemes, top 16 lenders, GST-based underwriting & application tips.",
    keywords: ["business loan without collateral", "unsecured business loan India", "MSME loan", "CGTMSE", "GST business loan"],
    category: "Business Loan Guides",
    author: "OneLoan Editorial",
    authorRole: "MSME Finance Team",
    date: "Dec 2, 2025",
    updated: "Dec 5, 2025",
    readTime: "12 min read",
    excerpt: "Collateral-free business loans up to ₹2 Crore are now mainstream in India. Here is the 2026 playbook: who qualifies, which 16 lenders are most active, how GST data has changed underwriting, and a 7-step application path that gets you funded in 7–10 days.",
    tags: ["Business Loan", "Unsecured", "MSME", "CGTMSE"],
    relatedProduct: "business",
    body: `India's MSME credit landscape has changed dramatically. Five years ago, a ₹50 Lakh business loan needed property collateral and 30 days of underwriting. In 2026, the same loan can be approved in 5–7 working days against GST data, current-account analytics and the CGTMSE government guarantee — with zero collateral. This is the complete playbook.

> **KEY TAKEAWAY:** If your business has 24+ months of GST filings, ₹40L+ turnover and a clean current account, you are likely eligible for an unsecured business loan up to ₹2 Crore at 13–22% from at least 5 lenders.

## Why collateral-free lending exploded

Three things converged: **digital GST data, the account-aggregator framework, and CGTMSE government guarantees**. Together they let lenders underwrite small businesses on real cash-flow signals rather than the value of mortgageable assets. The result — speed, scale and dramatically lower documentation.

## Unsecured business loan vs secured business loan

| Parameter | Unsecured (Collateral-free) | Secured (Property/FD backed) |
|---|---|---|
| Max amount | ₹2 Crore | ₹10 Crore+ |
| Interest rate | 13% – 22% | 9% – 16% |
| Tenure | 12 – 60 months | 12 – 180 months |
| Processing time | 5 – 10 days | 20 – 45 days |
| Documents | Light | Heavy + valuation |
| Best for | Working capital, marketing, hiring | Asset purchase, expansion |

For tickets up to ₹2 Crore, the time saved on unsecured lending usually outweighs the rate premium.

## Eligibility checklist

- **Business type:** Proprietorship, Partnership, LLP, Pvt Ltd
- **Vintage:** 2+ years (some lenders accept 12 months for digital businesses)
- **Turnover:** ₹40 Lakh+ annually (GST data preferred)
- **Owner age:** 25 – 65 years
- **Owner CIBIL:** 700+ (lenders also pull commercial CIBIL of the entity)
- **No recent NPA / loan default** in business or personal credit
- **Indian-registered entity** with valid GST or business proof

Quick check on our [Business Loan Eligibility Calculator](/tools/eligibility-calculator).

## Documents required (light, GST-led)

### KYC
- PAN of business + PAN of all promoters/partners
- Aadhaar of all promoters
- Business registration proof (GST cert, Udyam, Shop Act, Partnership Deed)

### Income proof
- **GST returns** — last 12 months
- **Current account bank statements** — last 12 months
- **ITR** — last 2 years (business + owner)
- **Audited financials** — for ticket sizes ₹50L+

### Bank-statement signals that improve approval
- Average monthly credit ≥ 1.5x the proposed EMI
- No EMI/cheque bounces in 12 months
- Stable inward credits from 5+ different counterparties
- Healthy closing balance trend

## Top 16 lenders for unsecured business loans (indicative 2026)

| Lender | Starting rate | Max amount | Vintage |
|---|---|---|---|
| HDFC Bank | 11.90% | ₹75 Lakh | 3 years |
| ICICI Bank | 12.50% | ₹2 Crore | 3 years |
| Axis Bank | 14.25% | ₹75 Lakh | 3 years |
| Kotak Mahindra | 16.00% | ₹75 Lakh | 2 years |
| IDFC FIRST | 14.50% | ₹1 Crore | 2 years |
| IndusInd Bank | 14.00% | ₹1 Crore | 3 years |
| Yes Bank | 13.50% | ₹75 Lakh | 3 years |
| Bandhan Bank | 14.00% | ₹50 Lakh | 2 years |
| Bajaj Finance | 14.00% | ₹80 Lakh | 3 years |
| Tata Capital | 14.00% | ₹75 Lakh | 2 years |
| Aditya Birla Finance | 13.00% | ₹1 Crore | 3 years |
| Axis Finance | 14.00% | ₹1 Crore | 3 years |
| Poonawalla Fincorp | 14.00% | ₹50 Lakh | 2 years |
| Piramal Finance | 15.00% | ₹50 Lakh | 2 years |
| InCred Finance | 14.00% | ₹50 Lakh | 2 years |
| SMFG India Credit | 14.50% | ₹75 Lakh | 2 years |

Compare instantly on our [Business Loan page](/business-loan).

## CGTMSE — the government guarantee that unlocks more credit

The Credit Guarantee Fund Trust for Micro and Small Enterprises (CGTMSE) provides a partial credit guarantee to lenders for collateral-free loans up to ₹2 Crore. This is the single biggest reason ₹2 Cr unsecured loans are now possible in India.

### How it benefits you
- Lender's risk is reduced → they say yes more often.
- You pay an annual **guarantee fee** of 0.85% – 1.5% on outstanding (variable by ticket).
- You avoid pledging property or FDs.

### Eligible borrower types
- Micro & Small Enterprises (per Udyam registration)
- Manufacturing units (investment up to ₹10 Cr, turnover up to ₹50 Cr)
- Service units (same limits)

Read our companion guide on [MSME government schemes](/learn/msme-loan-government-schemes) for PMMY, Stand-Up India and PMEGP.

## The 7-step application path

1. **Get a Udyam registration** if you don't already have one — free, online, takes 10 minutes.
2. **Pull your business + owner CIBIL** — fix any errors before applying.
3. **Organise documents** — KYC, GST returns, 12-month bank statement, ITR.
4. **Run an eligibility check on OneLoan** — soft pull, no CIBIL impact, see which lenders will approve.
5. **Submit one strong application** — not 5 simultaneous applications.
6. **Respond fast to underwriter queries** — most rejections happen because borrowers miss a follow-up email.
7. **Negotiate fees + rate** after sanction, before signing.

## How GST data changed the game

Lenders today pull 12-month GST returns directly via the GST APIs (with your consent). This gives them:
- True monthly turnover (filed GSTR-1)
- Tax payment patterns (GSTR-3B)
- Counterparty concentration (top buyers / suppliers)
- Seasonality and growth trend

A consistent GST filing record with growing turnover is now worth more to a lender than a 3-year-old audited balance sheet.

## Working capital vs term loan vs flexi OD — which to choose?

- **Term Loan:** One-time lump sum, fixed EMI. Best for hiring, marketing, equipment.
- **Working Capital Demand Loan:** Annual renewable line, repay anytime. Best for inventory.
- **Flexi Overdraft:** Limit sanctioned, interest on usage only. Best for seasonal cash flows.

Compare overdraft options on our [Flexi Overdraft Loan page](/overdraft-loan).

## Common rejection reasons

1. GST returns not filed in last 6 months
2. Bounced cheques or returned NACH in last 12 months
3. Owner CIBIL below 700 or recent NPA
4. Cash-heavy turnover that doesn't reflect in GST
5. Excessive existing borrowings (business + personal)
6. Inconsistent reported income (ITR vs GST mismatch >25%)

## Bottom line

Collateral-free is now the default for sub-₹2 Cr business borrowing in India. Be GST-compliant, keep your current account clean for 12 months, hold a 720+ CIBIL — and you'll see multiple lenders compete for your loan.

> **PRO TIP:** Apply once on [OneLoan Business Loan](/apply?type=business) and we'll match you with the top 3–5 lenders most likely to approve your profile, with side-by-side rate and fee comparisons.`,
    faqs: [
      { q: "What is the maximum unsecured business loan I can get in India in 2026?", a: "Up to ₹2 Crore is now standard for collateral-free business loans, supported by the CGTMSE government guarantee. A few banks like ICICI go up to ₹2 Cr unsecured for high-turnover MSMEs." },
      { q: "Is CGTMSE coverage mandatory for collateral-free business loans?", a: "No, but it is commonly used because it reduces the lender's risk and improves approval odds. The borrower pays an annual guarantee fee of 0.85–1.5% on outstanding." },
      { q: "Can I get a business loan if my business is only 1 year old?", a: "Yes — a few NBFCs (Bajaj Finance, InCred, Aditya Birla Finance) and select banks lend to 12-month-old businesses, especially digital-native ones with strong UPI/GST traction. Ticket size is usually capped at ₹25–50 Lakh." },
      { q: "Does my personal CIBIL matter for a business loan?", a: "Yes — both your personal CIBIL (700+) and the business's commercial CIBIL are pulled. The owner's personal credit profile is weighted heavily for unsecured business loans." },
      { q: "How fast can I get a ₹50 Lakh business loan?", a: "With clean GST returns, 12 months of bank statements, and a strong CIBIL profile, ₹50L unsecured business loans can be sanctioned in 5–7 working days and disbursed within 24 hours after signing." },
    ],
  },

  {
    slug: "msme-loan-government-schemes",
    title: "MSME Loan Government Schemes 2026: PMMY, CGTMSE, Stand-Up India & More",
    metaTitle: "MSME Loan Government Schemes 2026 — Complete India Guide",
    metaDescription: "Complete 2026 guide to India's MSME loan schemes — PMMY (Mudra), CGTMSE, Stand-Up India, PMEGP, CLCS-TUS. Eligibility, limits, participating banks.",
    keywords: ["MSME loan", "PMMY Mudra", "CGTMSE", "Stand-Up India", "PMEGP", "government business loan"],
    category: "Business Finance",
    author: "OneLoan Editorial",
    authorRole: "MSME Finance Team",
    date: "Nov 30, 2025",
    updated: "Dec 5, 2025",
    readTime: "11 min read",
    excerpt: "India's MSME schemes can give you cheaper, partially-guaranteed, sometimes subsidised credit — if you know where to apply. Here is a no-nonsense breakdown of every active scheme in 2026, who qualifies, and which lenders participate.",
    tags: ["MSME", "Government Schemes", "Mudra", "CGTMSE"],
    relatedProduct: "business",
    body: `India's government and public-sector banks run a dense web of MSME credit schemes. Most borrowers either don't know about them or get the wrong one. This guide cuts through the noise — every active scheme in 2026, its real eligibility criteria, the lenders that actually participate, and how OneLoan can route your application through the right scheme.

> **KEY TAKEAWAY:** PMMY (Mudra) is the easiest entry-level scheme up to ₹10 Lakh. For ₹10L – ₹2 Cr, CGTMSE-backed bank loans dominate. For specific demographics, Stand-Up India and PMEGP add subsidy layers on top.

## Quick comparison of major MSME schemes

| Scheme | Ticket size | Collateral | Best for |
|---|---|---|---|
| PMMY (Mudra) | Up to ₹10 Lakh | Nil | Micro businesses, traders |
| CGTMSE-backed | Up to ₹2 Crore | Nil | Small & medium enterprises |
| Stand-Up India | ₹10L – ₹1 Cr | Nil | Greenfield by SC/ST/Women |
| PMEGP | Up to ₹50L (mfg) / ₹20L (svc) | Margin only | New enterprises with subsidy |
| CLCS-TUS | Subsidy on term loan | — | Tech upgradation in MSMEs |
| Emergency Credit Line (ECLGS legacy) | Top-up on existing | Nil | Pandemic-affected revival |

## 1. PMMY — Pradhan Mantri Mudra Yojana

Mudra is India's flagship micro-enterprise scheme. Operated through banks, MFIs and NBFCs.

### Three tiers
- **Shishu:** up to ₹50,000 — for very small startups, vendors, tea-stalls
- **Kishor:** ₹50,001 – ₹5 Lakh — for established micro businesses
- **Tarun:** ₹5,00,001 – ₹10 Lakh — for growing enterprises
- **Tarun Plus** (new): ₹10L – ₹20L for successful Tarun graduates

### Eligibility
- Any non-corporate, non-farm small business (traders, manufacturers, service providers)
- No upper age limit
- No collateral required
- No fixed minimum CIBIL — but a clean credit history helps

### Typical use cases
- Inventory financing for retailers
- Equipment for service businesses
- Working capital for traders
- Vehicle for goods transport

### Participating lenders
All public sector banks, most private banks, RRBs, scheduled co-op banks, MFIs and select NBFCs.

## 2. CGTMSE — the biggest game-changer for ₹10L – ₹2 Cr

CGTMSE is not a "scheme that gives you a loan" — it is a **credit guarantee** that lenders can attach to your loan, letting them lend collateral-free up to ₹2 Crore.

### How it works
1. You apply for a normal business loan with a participating Bank / NBFC.
2. The lender approves and tags the loan under CGTMSE.
3. The government (via CGTMSE Trust) guarantees a portion of the lender's exposure.
4. You pay an annual guarantee fee of 0.85% – 1.5% on outstanding.

### Who's eligible
- Micro & Small Enterprises (Udyam registered)
- New and existing units
- Manufacturing investment up to ₹10 Cr or service turnover up to ₹50 Cr

### Why it matters
Without CGTMSE, lenders demand property collateral for ₹50L+ tickets. With it, you keep your assets free and still get funded.

## 3. Stand-Up India

Designed for women and SC/ST entrepreneurs setting up new (greenfield) enterprises.

### Key features
- Loan range: **₹10 Lakh to ₹1 Crore**
- Composite loan (term loan + working capital)
- 7-year tenure with 18-month moratorium
- Coverage: every scheduled commercial bank branch must lend to at least one SC/ST and one woman entrepreneur

### Eligibility
- SC/ST or Woman entrepreneur
- Age 18+
- First-time entrepreneur in the enterprise (greenfield)
- Holding 51%+ stake in non-individual entities

## 4. PMEGP — Prime Minister's Employment Generation Programme

PMEGP gives a **margin-money subsidy** to first-generation entrepreneurs setting up new ventures in manufacturing or services.

### Subsidy slabs
- Urban general category: 15%
- Urban special category (SC/ST/Women/PH/Ex-servicemen/Minorities): 25%
- Rural general: 25%
- Rural special: 35%

### Project ceilings
- Manufacturing: up to ₹50 Lakh
- Service / Trading: up to ₹20 Lakh

### Eligibility
- Age 18+
- 8th pass for projects above the lower threshold
- First-time entrepreneur, no prior PMEGP subsidy availed
- Applies via [kviconline.gov.in](/learn/msme-loan-government-schemes)

## 5. CLCS-TUS — Credit Linked Capital Subsidy for Tech Upgradation

A 15% capital subsidy on the term-loan portion used to buy approved machinery in any of 51 sub-sectors.

### Highlights
- Subsidy up to ₹15 Lakh per unit
- For SSI units adopting newer technology
- Routed through nodal banks: SIDBI, NABARD, etc.

## 6. SIDBI direct schemes

SIDBI runs several niche schemes for MSMEs:
- **SMILE:** loan for new MSMEs (₹25L – ₹25 Cr)
- **SPEED:** equipment loans (up to ₹2 Cr at 9.25%)
- **STAR:** Special Trade Advance for retailers

## How to apply — practical path

1. **Get your Udyam registration first.** Free, online, 10 minutes. Every MSME scheme requires it.
2. **Match the scheme to your need:**
   - Need < ₹10L? → PMMY (Mudra)
   - Need ₹10L – ₹2 Cr? → CGTMSE-tagged business loan
   - Greenfield + SC/ST/Woman? → Stand-Up India
   - New venture + want subsidy? → PMEGP
3. **Apply through OneLoan** — we route your file to the right lender under the right scheme.
4. **Be ready with:** PAN, Aadhaar, Udyam, GST/ITR if applicable, project report (for greenfield), bank statement.

## Common myths

> **NOTE:** Government doesn't directly give you the loan. The loan comes from a Bank/NBFC. Government provides either a guarantee (CGTMSE) or a subsidy (PMEGP) or a mandate to the bank to consider you (Stand-Up India).

> **WARNING:** Avoid agents charging "scheme processing fees" upfront. All government schemes are free to apply for. Any legitimate fee is a one-time processing charge by the lender, deducted from disbursal.

## Interest rates under government schemes — realistic ranges

| Scheme | Rate band 2026 |
|---|---|
| PMMY Shishu | 8% – 12% |
| PMMY Kishor / Tarun | 10% – 14% |
| CGTMSE-tagged loans | 10.5% – 16% |
| Stand-Up India | 8.5% – 11% |
| PMEGP-linked term loan | 10% – 14% |
| SIDBI direct (SPEED) | 8.75% – 10.5% |

> **PRO TIP:** Stack a CGTMSE guarantee on top of a SIDBI SPEED loan, and you can finance ₹2 Cr of equipment at sub-10% — entirely collateral-free. Few brokers will tell you this; OneLoan will.

## Bottom line

Government schemes can lower your cost of capital by 200–400 basis points and remove collateral requirements. The right scheme depends entirely on your stage, demographic and use of funds. Run a 5-minute eligibility check on [OneLoan Business Loan](/apply?type=business) and we'll show you which schemes apply to your profile.`,
    faqs: [
      { q: "What is the maximum Mudra loan I can get in 2026?", a: "Up to ₹10 Lakh under standard Mudra (Tarun tier). Successful Tarun borrowers can graduate to Tarun Plus for ₹10L–₹20L. Above that, you typically move to CGTMSE-backed business loans." },
      { q: "Is CGTMSE a loan or a guarantee?", a: "CGTMSE is a credit guarantee, not a loan. It guarantees a portion of the lender's exposure, enabling collateral-free loans up to ₹2 Crore. You still borrow from a Bank/NBFC and pay an annual guarantee fee." },
      { q: "Who qualifies for Stand-Up India?", a: "SC/ST entrepreneurs and women entrepreneurs (age 18+) setting up greenfield (new) enterprises in manufacturing, services or trading. Loan ticket: ₹10 Lakh to ₹1 Crore." },
      { q: "How much subsidy does PMEGP provide?", a: "15–35% margin-money subsidy on project cost, depending on rural/urban location and applicant category. Manufacturing projects up to ₹50 Lakh and service/trading up to ₹20 Lakh are eligible." },
      { q: "Can I combine CGTMSE with another scheme like SIDBI SPEED?", a: "Yes. CGTMSE is a guarantee mechanism that can be tagged on top of many lender loan products, including SIDBI direct schemes like SPEED and SMILE — letting you borrow large amounts collateral-free at attractive rates." },
    ],
  },
];
